// @ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import ImageUploadFlow, { ImageUploadTrigger } from "../imageUploader/ImageUploadFlow";
import ProfilePictureRounded from "../ProfilePictureRounded";
import { imageCache } from "../../utils/imageCache";

// Text/textarea fields shown on a public profile, each gated by its own visibility toggle. Display name is `alwaysOn` — a public profile must always have a name (never "Unnamed"). (Profile picture is handled separately via the image upload flow.)
const TEXT_FIELDS = [
  { key: "displayName", toggle: "showDisplayName", label: "Display name", type: "text", alwaysOn: true },
  { key: "email", toggle: "showEmail", label: "Email", type: "email" },
  { key: "bio", toggle: "showBio", label: "Bio", type: "textarea" },
];

const ALL_FIELDS = [...TEXT_FIELDS, { key: "profilePictureUrl", toggle: "showProfilePicture" }];

const ALWAYS_ON_TOGGLES = TEXT_FIELDS.filter((f) => f.alwaysOn).map((f) => f.toggle);

// Build a mutation input from form state. A field whose toggle is OFF is written as null so hidden values are genuinely absent from the public record. `alwaysOn` fields are always published.
export const buildPublicProfileInput = (id, values, toggles) => {
  const input = { id };
  ALL_FIELDS.forEach(({ key, toggle }) => {
    const on = ALWAYS_ON_TOGGLES.includes(toggle) ? true : !!toggles[toggle];
    input[key] = on ? (values[key] || null) : null;
    input[toggle] = on;
  });
  return input;
};

// Reusable editor for a single public profile (user or organization). `lockedFields` are read-only (value comes from the account/org) — used to prevent identity/email spoofing. `uploadFolder` is the S3 sub-path under the uploader's protected/ prefix for the picture.
const PublicProfileEditor = ({
  title,
  subtitle,
  profile,
  defaults,
  saving,
  onSave,
  id,
  uploadFolder,
  lockedFields = [],
  error,
}) => {
  const [values, setValues] = useState({ displayName: "", email: "", bio: "", profilePictureUrl: "" });
  const [toggles, setToggles] = useState({
    showDisplayName: true,
    showEmail: false,
    showBio: true,
    showProfilePicture: true,
  });
  const [showImageModal, setShowImageModal] = useState(false);
  const [previewSignedUrl, setPreviewSignedUrl] = useState(null);

  // Prefill from the loaded profile when it arrives.
  useEffect(() => {
    if (profile) {
      setValues({
        displayName: profile.displayName || "",
        email: profile.email || "",
        bio: profile.bio || "",
        profilePictureUrl: profile.profilePictureUrl || "",
      });
      setToggles({
        showDisplayName: profile.showDisplayName ?? true,
        showEmail: profile.showEmail ?? false,
        showBio: profile.showBio ?? true,
        showProfilePicture: profile.showProfilePicture ?? true,
      });
    }
  }, [profile]);

  // When creating (no profile yet), seed the form from defaults exactly once so subsequent keystrokes aren't overwritten on re-render (defaults is a fresh object each render).
  const seededDefaults = useRef(false);
  useEffect(() => {
    // Wait for real account data before seeding (locked fields would otherwise lock in blanks).
    if (!profile && defaults && !seededDefaults.current && Object.values(defaults).some(Boolean)) {
      seededDefaults.current = true;
      setValues((v) => ({ ...v, ...defaults }));
    }
  }, [profile, defaults]);

  // Keep a signed preview URL in sync with the stored S3 path.
  useEffect(() => {
    if (!values.profilePictureUrl) {
      setPreviewSignedUrl(null);
      return;
    }
    imageCache.getSignedUrl(values.profilePictureUrl).then((u) => setPreviewSignedUrl(u || null));
  }, [values.profilePictureUrl]);

  const [validationError, setValidationError] = useState(null);

  // On save, locked fields are sourced from the account/org (defaults), never the editable state, so hiding then re-showing them never loses the value.
  const handleSave = () => {
    const merged = { ...values };
    lockedFields.forEach((k) => {
      merged[k] = defaults?.[k] ?? "";
    });
    // Display name is always public and must never be blank.
    if (!merged.displayName || !merged.displayName.trim()) {
      setValidationError("A display name is required.");
      return;
    }
    setValidationError(null);
    onSave(merged, toggles);
  };

  // Visibility reads as one chip that states the current state, matching the settings page's segmented toggles — a bare checkbox in a form row is the rawest control on the page.
  const VisibilityToggle = ({ on, onToggle }) => (
    <button
      type="button"
      onClick={onToggle}
      className={`eai-chip shrink-0 ${on ? "eai-chip-active" : ""}`}
      aria-pressed={on}
    >
      {on ? "Shown publicly" : "Hidden"}
    </button>
  );

  return (
    <section>
      <h2 className="eai-panel-title">{title}</h2>
      {subtitle && <p className="eai-description-sm mt-0.5">{subtitle}</p>}

      <div className="eai-card mt-3 divide-y divide-eai-border overflow-hidden">
        {TEXT_FIELDS.map(({ key, toggle, label, type, alwaysOn }) => {
          const locked = lockedFields.includes(key);
          const disabled = locked || (!alwaysOn && !toggles[toggle]);
          // Locked fields always reflect the live account/org value (defaults), never the editable state or a stored null — so toggling visibility off and back on keeps the value.
          const fieldValue = locked ? (defaults?.[key] ?? "") : values[key];
          return (
            <div key={key} className="px-5 py-4">
              <div className="mb-2 flex items-center justify-between gap-3">
                <label htmlFor={`ppe-${key}`} className="eai-meta-label">
                  {label}
                </label>
                {alwaysOn ? (
                  <span className="eai-tag-pill-sm shrink-0">Always shown</span>
                ) : (
                  <VisibilityToggle
                    on={!!toggles[toggle]}
                    onToggle={() => setToggles((t) => ({ ...t, [toggle]: !t[toggle] }))}
                  />
                )}
              </div>
              {type === "textarea" ? (
                <textarea
                  id={`ppe-${key}`}
                  className="eai-input resize-y"
                  rows={3}
                  value={fieldValue}
                  disabled={disabled}
                  onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                />
              ) : (
                <input
                  id={`ppe-${key}`}
                  type={type}
                  className="eai-input"
                  value={fieldValue}
                  disabled={disabled}
                  onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                />
              )}
              {locked && (
                <p className="mt-1.5 font-inter text-eai-body-xs text-eai-ink-faint">
                  Pulled from the account — not editable here.
                </p>
              )}
            </div>
          );
        })}

        {/* Profile picture — preview + selector, reusing the shared image upload flow. */}
        <div className="px-5 py-4">
          <div className="mb-2 flex items-center justify-between gap-3">
            <span className="eai-meta-label">Profile picture</span>
            <VisibilityToggle
              on={!!toggles.showProfilePicture}
              onToggle={() =>
                setToggles((t) => ({ ...t, showProfilePicture: !t.showProfilePicture }))
              }
            />
          </div>
          <ImageUploadTrigger
            label=""
            previewNode={
              values.profilePictureUrl ? (
                <ProfilePictureRounded pictureURL={values.profilePictureUrl} diameter={64} />
              ) : undefined
            }
            previewUrl={previewSignedUrl}
            onClick={() => setShowImageModal(true)}
            buttonText={values.profilePictureUrl ? "Change Image" : "Choose Image"}
            placeholderText="No image"
          />
        </div>

        {/* Save sits in the card's own footer bar, the way a modal's pinned footer reads */}
        <div className="flex items-center justify-end gap-3 bg-eai-surface-sunken px-5 py-4">
          {(validationError || error) && (
            <span className="mr-auto font-inter text-eai-body-sm text-eai-danger-text">
              {validationError || error}
            </span>
          )}
          <button onClick={handleSave} disabled={saving} className="eai-btn-cta">
            {saving ? "Saving…" : profile ? "Save changes" : "Create public profile"}
          </button>
        </div>
      </div>

      {showImageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="eai-card max-h-[90dvh] w-full max-w-2xl overflow-y-auto p-5">
            <ImageUploadFlow
              asset={{ id, name: title, imageUrl: values.profilePictureUrl }}
              uploadPathBuilder={({ identityId, file }) =>
                `protected/${identityId}/${uploadFolder}/${file.name}`
              }
              onUploadSuccess={async ({ imageUrl }) => {
                if (values.profilePictureUrl) imageCache.invalidate(values.profilePictureUrl);
                setValues((v) => ({ ...v, profilePictureUrl: imageUrl }));
                setShowImageModal(false);
              }}
              onClose={() => setShowImageModal(false)}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default PublicProfileEditor;
